import { request, requestWithMsg, MsgResult, PageResult } from './request';

// 写接口统一走 requestWithMsg: 统一由后端转成审批工单
// (message 含"已提交审批", data={taskCode}), 调用点用 alertApprovalIfPending 提示.
export type ToolWriteResult = MsgResult<{ taskCode?: string } | null>;

export type ToolStatus = 'dev' | 'live';

/** 绑定了该换算服务的设备概要 (device.convert_tool_key 反查; 列表/详情接口附带). */
export interface ToolBoundDevice {
    deviceKey: string;
    name?: string;
}

export interface Tool {
    id: number;
    uuid: string;
    toolKey: string;
    name: string;
    icon?: string;
    category?: string;
    description?: string;
    endpoint?: string;
    svcLabel?: string;
    inputSchema?: string;
    outputSchema?: string;
    status: ToolStatus;
    isInternal: number;
    /** 绑定了本服务的设备清单 (换算服务被哪些设备引用; 只读, 绑定在设备侧维护). */
    boundDevices?: ToolBoundDevice[];
    createdBy: number;
    updatedBy: number;
    remark?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface ListToolsParams {
    keyword?: string;
    category?: string;
    status?: ToolStatus;
    isInternal?: number;
    page?: number;
    pageSize?: number;
}

const BASE = '/v1';

export function listTools(params: ListToolsParams): Promise<PageResult<Tool>> {
    return request<PageResult<Tool>>(`${BASE}/tools`, { query: params });
}

export function getTool(toolKey: string): Promise<Tool> {
    return request<Tool>(`${BASE}/tools/detail`, { method: 'POST', dedupe: true, body: { toolKey } });
}

export function createTool(
    payload: Partial<Tool> & { toolKey: string; name: string },
): Promise<MsgResult<{ id?: number; toolKey?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/tools`, { method: 'POST', body: payload });
}

export function updateTool(toolKey: string, payload: Partial<Tool>): Promise<ToolWriteResult> {
    return requestWithMsg(`${BASE}/tools/update`, { method: 'POST', body: { toolKey, ...payload } });
}

export function changeToolStatus(
    toolKey: string,
    status: ToolStatus,
): Promise<MsgResult<{ toolKey?: string; status?: string; taskCode?: string }>> {
    // 业务键走 body (后端 statusReq.toolKey 必填)
    return requestWithMsg(`${BASE}/tools/status`, {
        method: 'POST',
        body: { toolKey, status },
    });
}

export function deleteTool(toolKey: string): Promise<ToolWriteResult> {
    return requestWithMsg(`${BASE}/tools/delete`, { method: 'POST', body: { toolKey } });
}

// ---- 统一 Tool API (§7.8) ------------------------------------------------

/** 工具调用透传结果: 工具自身的 HTTP 状态与原样响应 (平台不解释工具协议). */
export interface InvokeOutcome {
    toolKey: string;
    httpStatus: number;
    elapsedMs: number;
    result: unknown;
}

/**
 * 统一调用注册工具: 平台按注册表把 input 透传到工具 endpoint.
 * live 所有登录用户可调; dev 状态仅管理员 (上线前灰度验证).
 * 对外第三方走 POST /openapi/v1/tools/invoke (Bearer mlk_ token), 协议一致.
 */
export function invokeTool(toolKey: string, input: unknown): Promise<InvokeOutcome> {
    // 后端把整个 body 原样透传给工具, 并从中解出 toolKey (2026-08-24 键从路径挪到 body)
    return request<InvokeOutcome>(`${BASE}/tools/invoke`, {
        method: 'POST',
        body: { toolKey, ...(typeof input === 'object' && input !== null ? input : {}) },
    });
}
